/**
 * Controle de geração de requisições assíncronas.
 * Respostas de gerações anteriores devem ser ignoradas.
 */
export const createRequestGuard = () => {
  let generation = 0;

  return {
    begin: () => {
      generation += 1;
      return generation;
    },
    isCurrent: (token) => token === generation,
    /** Invalida qualquer resposta em voo (logout / unmount). */
    invalidate: () => {
      generation += 1;
      return generation;
    },
    get current() {
      return generation;
    },
  };
};

export const buildBreakageSessionKey = (companyId, identityKey) =>
  `${companyId ?? ""}::${identityKey ?? ""}`;

export const isValidBreakageSession = (companyId, identityKey) => {
  const company =
    companyId !== undefined && companyId !== null && String(companyId).trim() !== "";
  const identity =
    identityKey !== undefined &&
    identityKey !== null &&
    String(identityKey).trim() !== "";
  return company && identity;
};

/**
 * Em API real, o Owner do payload deve coincidir com a empresa da sessão UI.
 * Mock não exige match (payload fixo do mock).
 */
export const reconcileEntitlementWithSession = (
  result,
  {
    expectedCompanyId,
    sessionKey,
    requireOwnerMatch = true,
  } = {}
) => {
  const entitlement = result?.entitlement || result?.data || null;
  const base = {
    loading: false,
    entitlement,
    sessionKey,
  };

  if (!result) {
    return {
      ...base,
      kind: "unavailable",
      enabled: false,
      unauthorized: false,
      forbidden: false,
      unavailable: true,
      scopeMismatch: false,
      error: "Resposta de entitlement vazia",
    };
  }

  const kind = result.kind || "unavailable";

  // Respostas comerciais autoritativas (200 enabled/disabled) devem
  // pertencer ao Owner da sessão. Erros 401/403/503 não passam por aqui
  // como "enabled"/"disabled" e não exigem comparação de Owner.
  const isAuthoritativeCommercial =
    kind === "enabled" || kind === "disabled";

  if (
    requireOwnerMatch &&
    isAuthoritativeCommercial &&
    expectedCompanyId != null &&
    String(expectedCompanyId).trim() !== ""
  ) {
    const returnedOwner = entitlement?.ownerCompanyId;
    if (
      returnedOwner == null ||
      String(returnedOwner).trim() === "" ||
      String(returnedOwner) !== String(expectedCompanyId)
    ) {
      return {
        ...base,
        kind: "scope_mismatch",
        enabled: false,
        unauthorized: false,
        forbidden: false,
        unavailable: false,
        scopeMismatch: true,
        error:
          "O entitlement retornado não corresponde à empresa selecionada na sessão. Atualize o login ou troque novamente de empresa.",
      };
    }
  }

  return {
    ...base,
    kind,
    enabled: kind === "enabled",
    unauthorized: kind === "unauthorized",
    forbidden: kind === "forbidden",
    unavailable: kind === "unavailable",
    scopeMismatch: false,
    error: result.error || null,
  };
};

/** @deprecated use reconcileEntitlementWithSession */
export const mapEntitlementResultToState = (result, sessionKey) =>
  reconcileEntitlementWithSession(result, {
    sessionKey,
    requireOwnerMatch: false,
  });
